import { useState, useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { FormField, Input, Label, FormError } from '../ui/input';
import { CurrencyInput } from '../ui/currency-input';
import { DateInput } from '../ui/date-input';
import { Select } from '../ui/select-custom';
import { toISODate, fromISODate } from '../../lib/date';
import { formatCurrency, formatQuantity } from '../../lib/format';
import { availableQuantityAt } from '../../lib/investments';
import type { PortfolioInput } from '../../lib/investments';
import type { InvestmentAsset, Trade } from '../../types';
import type { ComponentProps } from 'react';

export interface TradeFormData {
  assetId: string;
  operation: 'buy' | 'sell';
  date: Date;
  quantity: number;
  unitPrice: number;
  fees: number;
  irrfWithheld: number;
  notes: string;
}

export interface TradeFormProps extends Omit<ComponentProps<'form'>, 'onSubmit'> {
  onSubmit: (data: TradeFormData) => void;
  onClose?: () => void;
  initialData?: Trade | null;
  assets: InvestmentAsset[];
  /** Histórico completo, para validar a quantidade disponível na data da venda. */
  portfolio: PortfolioInput;
  defaultAssetId?: string;
}

export function TradeForm({
  onSubmit,
  onClose,
  initialData,
  assets,
  portfolio,
  defaultAssetId,
  ...props
}: TradeFormProps) {
  const [assetId, setAssetId] = useState(
    initialData?.assetId ?? defaultAssetId ?? assets[0]?.id ?? ''
  );
  const [operation, setOperation] = useState<'buy' | 'sell'>(
    initialData?.operation ?? 'buy'
  );
  const [dateISO, setDateISO] = useState(
    toISODate(initialData?.date ?? new Date())
  );
  const [quantity, setQuantity] = useState(
    initialData ? String(initialData.quantity) : ''
  );
  const [unitPrice, setUnitPrice] = useState(initialData?.unitPrice ?? 0);
  const [fees, setFees] = useState(initialData?.fees ?? 0);
  const [irrfWithheld, setIrrfWithheld] = useState(initialData?.irrfWithheld ?? 0);
  const [notes, setNotes] = useState(initialData?.notes ?? '');

  const isEditing = !!initialData;

  useEffect(() => {
    setAssetId(initialData?.assetId ?? defaultAssetId ?? assets[0]?.id ?? '');
    setOperation(initialData?.operation ?? 'buy');
    setDateISO(toISODate(initialData?.date ?? new Date()));
    setQuantity(initialData ? String(initialData.quantity) : '');
    setUnitPrice(initialData?.unitPrice ?? 0);
    setFees(initialData?.fees ?? 0);
    setIrrfWithheld(initialData?.irrfWithheld ?? 0);
    setNotes(initialData?.notes ?? '');
  }, [initialData, defaultAssetId, assets]);

  const quantityNumber = Number(quantity.replace(',', '.'));
  const hasQuantity = quantity.trim() !== '' && Number.isFinite(quantityNumber) && quantityNumber > 0;
  const date = useMemo(() => (dateISO ? fromISODate(dateISO) : null), [dateISO]);

  // Quantidade disponível na data, desconsiderando a própria operação em edição.
  const available = useMemo(() => {
    if (!assetId || !date) return 0;
    return availableQuantityAt(portfolio, assetId, date, initialData?.id);
  }, [portfolio, assetId, date, initialData?.id]);

  const exceedsPosition =
    operation === 'sell' && hasQuantity && quantityNumber > available + 1e-8;

  const selectedAsset = assets.find((a) => a.id === assetId);
  const gross = hasQuantity ? quantityNumber * unitPrice : 0;
  const total = operation === 'buy' ? gross + fees : gross - fees;

  const isFormValid =
    assetId !== '' && hasQuantity && unitPrice > 0 && !!date && !exceedsPosition;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid || !date) return;
    onSubmit({
      assetId,
      operation,
      date,
      quantity: quantityNumber,
      unitPrice,
      fees,
      irrfWithheld: operation === 'sell' ? irrfWithheld : 0,
      notes: notes.trim(),
    });
    if (!isEditing) {
      setQuantity('');
      setUnitPrice(0);
      setFees(0);
      setIrrfWithheld(0);
      setNotes('');
    }
  };

  return (
    <form
      data-slot="trade-form"
      onSubmit={handleSubmit}
      className="flex flex-col gap-4"
      {...props}
    >
      <div className="grid grid-cols-2 gap-4">
        <FormField>
          <Label>
            Papel <span className="text-destructive">*</span>
          </Label>
          <Select
            value={assetId}
            onChange={setAssetId}
            options={assets.map((a) => ({ value: a.id, label: a.ticker }))}
            placeholder="Selecione o papel"
            className="w-full justify-between"
          />
        </FormField>

        <FormField>
          <Label>
            Operação <span className="text-destructive">*</span>
          </Label>
          <Select
            value={operation}
            onChange={(value) => setOperation(value as 'buy' | 'sell')}
            options={[
              { value: 'buy', label: 'Compra' },
              { value: 'sell', label: 'Venda' },
            ]}
            className="w-full justify-between"
          />
        </FormField>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormField>
          <Label htmlFor="trade-date">
            Data <span className="text-destructive">*</span>
          </Label>
          <DateInput id="trade-date" value={dateISO} onChange={setDateISO} />
        </FormField>

        <FormField>
          <Label htmlFor="quantity">
            Quantidade <span className="text-destructive">*</span>
          </Label>
          <Input
            id="quantity"
            value={quantity}
            placeholder="Ex: 100"
            inputMode="decimal"
            onChange={(e) => setQuantity(e.target.value.replace(/[^0-9.,]/g, ''))}
            error={exceedsPosition}
            required
          />
          {operation === 'sell' && (
            <span className="text-xs text-muted-foreground">
              Disponível em {date?.toLocaleDateString('pt-BR')}: {formatQuantity(available)}
            </span>
          )}
          {exceedsPosition && (
            <FormError>
              Venda maior que a posição na data ({formatQuantity(available)} disponível).
            </FormError>
          )}
        </FormField>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormField>
          <Label htmlFor="unitPrice">
            Valor unitário <span className="text-destructive">*</span>
          </Label>
          <CurrencyInput id="unitPrice" value={unitPrice} onChange={setUnitPrice} required />
        </FormField>

        <FormField>
          <Label htmlFor="fees">Taxas</Label>
          <CurrencyInput id="fees" value={fees} onChange={setFees} />
          <span className="text-xs text-muted-foreground">
            Corretagem + emolumentos. Entra no custo médio.
          </span>
        </FormField>
      </div>

      {operation === 'sell' && (
        <FormField>
          <Label htmlFor="irrf">IRRF retido na venda</Label>
          <CurrencyInput id="irrf" value={irrfWithheld} onChange={setIrrfWithheld} />
          <span className="text-xs text-muted-foreground">
            O “dedo-duro” de 0,005% que aparece na nota. Compensa o imposto do mês.
          </span>
        </FormField>
      )}

      <FormField>
        <Label htmlFor="notes">Observação</Label>
        <Input
          id="notes"
          value={notes}
          placeholder="Opcional"
          onChange={(e) => setNotes(e.target.value)}
        />
      </FormField>

      {hasQuantity && unitPrice > 0 && (
        <div className="rounded-lg bg-muted px-3 py-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">
              {operation === 'buy' ? 'Custo total da compra' : 'Valor líquido da venda'}
            </span>
            <span className="font-semibold text-foreground">
              R$ {formatCurrency(total)}
            </span>
          </div>
          {selectedAsset && operation === 'sell' && (
            <div className="mt-1 text-xs text-muted-foreground">
              O resultado apurado usa o custo médio de {selectedAsset.ticker} na data.
            </div>
          )}
        </div>
      )}

      <div className="flex gap-2">
        {onClose && (
          <Button type="button" variant="outline" className="flex-1" onClick={onClose}>
            Cancelar
          </Button>
        )}
        <Button type="submit" className="flex-1" disabled={!isFormValid}>
          {isEditing ? 'Salvar alterações' : 'Registrar operação'}
        </Button>
      </div>
    </form>
  );
}
